import { useLocation, useNavigate, useNavigationType, type Path } from "react-router";
import { useEffect, useState } from "react";
import { RawStateT, StateT } from "../product-comparison-page-component";
import { pipe } from "fp-ts/lib/function";
import { arrayOfMaxLength3 } from "./utils";
import { array, nonEmptyArray, option } from "fp-ts";
import { ExtractSwarovskiPageData, ProductComparisonPageData } from "@swo/storefront";
import { Eq as eqString } from "fp-ts/es6/string";
import type { SwarovskiLinkData } from "../../../components/_shared-components/ui-elements/SwarovskiLink";

export function usePCPState(products: ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["products"]) {
  const navigate = useNavigate();
  const location = useLocation();
  const fallbackProduct = nonEmptyArray.head(products);

  function initializeState(locationSearch: string): StateT {
    const readUrlState = readUrlStateToState(locationSearch);

    return {
      ...readUrlState,
      selectedProducts: pipe(
        readUrlState.selectedProducts,
        array.filterMap(rawSelectedProductKey =>
          option.fromNullable(products.find(p => p.product.key === rawSelectedProductKey)),
        ),
        nonEmptyArray.fromArray,
        option.getOrElse(() => {
          return nonEmptyArray.of(fallbackProduct);
        }),
      ),
    };
  }

  const [state, setState] = useState<StateT>(initializeState(location.search));

  const navigationType = useNavigationType();

  useEffect(() => {
    const paramsStr = serializeStateToUrl(state).toString();

    if (`?${paramsStr}` !== location.search) {
      void navigate({
        ...location,
        search: paramsStr,
      });
    }
  }, [state]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (navigationType === "POP") {
      setState(() => {
        return initializeState(location.search);
      });
    }
  }, [location.search]);

  function selectedProductsUpdated(selectedProduct: StateT["selectedProducts"][0], idx: number) {
    setState(state => {
      let t = [...state.selectedProducts];
      t.splice(idx, 1, selectedProduct);

      const ret = pipe(
        t,
        nonEmptyArray.fromArray,
        option.getOrElse(() => {
          return nonEmptyArray.of(fallbackProduct);
        }),
      );

      return {
        ...state,
        selectedProducts: validateSelectedProducts(ret, fallbackProduct),
      };
    });
  }

  function selectedNewProduct(selectedProduct: StateT["selectedProducts"][0]) {
    setState(state => {
      if (state.selectedProducts.length >= 3) {
        return state;
      } else {
        return {
          ...state,
          selectedProducts: validateSelectedProducts(
            nonEmptyArray.concat([...state.selectedProducts], nonEmptyArray.of(selectedProduct)),
            fallbackProduct,
          ),
        };
      }
    });
  }

  function onRemoveRequested(idx: number) {
    setState(state => {
      const arr = pipe(
        [...state.selectedProducts],
        array.mapWithIndex((i, a) => {
          if (idx === i) {
            return option.none;
          } else {
            return option.some(a);
          }
        }),
        array.compact,
        nonEmptyArray.fromArray,
        option.getOrElse(() => {
          return nonEmptyArray.of(fallbackProduct);
        }),
      );

      return {
        ...state,
        selectedProducts: validateSelectedProducts(arr, fallbackProduct),
      };
    });
  }

  function validateSelectedProducts(
    selectedProducts: StateT["selectedProducts"],
    fallbackProduct: StateT["selectedProducts"][0],
  ): StateT["selectedProducts"] {
    return pipe(
      selectedProducts,
      arrayOfMaxLength3,
      array.uniq({
        equals: (a, b) => a.product.key === b.product.key,
      }),
      nonEmptyArray.fromArray,
      option.getOrElse(() => {
        return nonEmptyArray.of(fallbackProduct);
      }),
    );
  }

  function toggleMode() {
    setState(state => {
      return {
        ...state,
        mode: state.mode === "all" ? "differences-only" : "all",
      };
    });
  }

  return [state, selectedProductsUpdated, selectedNewProduct, onRemoveRequested, toggleMode] as const;
}

export const QUERYSTRING_PARAM = "s";

function readUrlStateToState(locationSearch: Path["search"]): RawStateT {
  const params = new URLSearchParams(locationSearch);

  const selectedProducts = pipe(params.getAll(QUERYSTRING_PARAM), arrayOfMaxLength3, array.uniq(eqString));

  return {
    for: locationSearch,
    selectedProducts: selectedProducts,
    mode: "all",
  };
}

function serializeStateToUrl(state: StateT): URLSearchParams {
  const selectedProducts = state.selectedProducts;

  let params = new URLSearchParams();
  selectedProducts.forEach(product => {
    params.append(QUERYSTRING_PARAM, product.product.key);
  });

  return params;
}

export function getCleanProductComparisonPageLink(
  pageComparisonBaseLink: string,
  selectedProductKey: string,
): SwarovskiLinkData {
  return {
    pathname: pageComparisonBaseLink,
    search: `?${QUERYSTRING_PARAM}=${selectedProductKey}`,
  };
}
